import { endpoints } from '~/api/endpoints';
import { integrationsSseBaseUrl } from '~/constants/config/env';
import { useAuthStore } from '~/store/auth.store';
import { useLocaleStore } from '~/store/locale.store';

import type { JsonMap } from '../types/delivery-runs.types';

/**
 * Stream-level error carrying the upstream HTTP status and any Retry-After
 * hint. Duck-typed on `.status` by the consumer so the check survives
 * module-graph duplication. Mirrors `ExportStreamError` / `NotificationsStreamError`.
 */
export class DeliveryStreamError extends Error {
  readonly status: number;
  readonly retryAfterSeconds?: number;
  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'DeliveryStreamError';
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** One parsed SSE frame: the `event:` name (default `message`) and its JSON `data:`. */
export interface DeliveryStreamEvent {
  event: string;
  data: JsonMap;
}

/** Parse a Retry-After header (delta-seconds or HTTP-date) into seconds; undefined when absent/malformed. */
function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;
  const trimmed = headerValue.trim();
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number.parseInt(trimmed, 10);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
  }
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  const deltaMs = dateMs - Date.now();
  return deltaMs > 0 ? Math.ceil(deltaMs / 1000) : 0;
}

export interface DeliveryRunStreamOptions {
  runId: string;
  /** Fired once after the server returns 200 — the consumer resets its backoff on this. */
  onOpen?: () => void;
  /** Fired for each parsed, non-heartbeat frame. */
  onEvent: (event: DeliveryStreamEvent) => void;
  /** Stream-level error (network, 401/403, 429, 5xx, or a server `error` frame). Called once. */
  onError: (error: Error) => void;
  /** The server closed the stream cleanly. */
  onComplete: () => void;
  signal?: AbortSignal;
}

/**
 * Opens the live-progress SSE stream for one delivery run
 * (`GET /deliveries/runs/{runId}/stream`). Raw `fetch()` rather than Ky so the
 * response isn't buffered, auth/tenant/locale headers read from the stores at
 * connect time, manual `\n\n` framing. Frame names aren't pinned by the
 * contract, so every non-`ping` frame with JSON data is forwarded and the
 * consumer decides what to merge. Returns an `AbortController` for teardown.
 */
export function createDeliveryRunStream(options: DeliveryRunStreamOptions): AbortController {
  const { runId, onOpen, onEvent, onError, onComplete, signal } = options;
  const controller = new AbortController();

  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        controller.abort();
      },
      { once: true },
    );
  }

  // Path from `endpoints` (URLs live in one place); base from env so prod bypasses the CDN.
  const fullUrl = `${integrationsSseBaseUrl}/${endpoints.deliveries.runStream(runId)}`;
  const { user, accessToken } = useAuthStore.getState();
  const tenantId = user?.activeTenantId;
  const locale = useLocaleStore.getState().activeLocale;

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': locale,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (tenantId) headers['X-Subsidiary-ID'] = tenantId;

  void (async () => {
    try {
      const response = await fetch(fullUrl, { method: 'GET', headers, signal: controller.signal });

      if (!response.ok) {
        throw new DeliveryStreamError(
          response.status,
          `Delivery run SSE stream failed: ${String(response.status)} ${response.statusText}`,
          parseRetryAfter(response.headers.get('Retry-After')),
        );
      }
      if (!response.body) {
        throw new Error('Response body is null — streaming not supported');
      }

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processBlock(buffer, onEvent, onError);
          onComplete();
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          if (part.trim()) processBlock(part, onEvent, onError);
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/**
 * Parse one SSE block. `ping`/`heartbeat` frames and malformed JSON are dropped;
 * a server `error` frame is surfaced through `onError` so the consumer can fall
 * back to polling; everything else is forwarded with its event name.
 */
function processBlock(
  block: string,
  onEvent: (event: DeliveryStreamEvent) => void,
  onError: (error: Error) => void,
): void {
  let eventType = 'message';
  let dataStr = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }

  if (eventType === 'ping' || eventType === 'heartbeat' || !dataStr) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataStr);
  } catch {
    return; // malformed JSON — backend bug, not user-actionable
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return;
  const data = parsed as JsonMap;

  if (eventType === 'error') {
    const message = typeof data.message === 'string' ? data.message : 'Stream reported an error';
    onError(new Error(message));
    return;
  }
  onEvent({ event: eventType, data });
}
